import Regex from 'constants/regex';
import React from 'react';

/**
 * Hook for subscribing to input change
 * @param {(val: string) => void} onChange
 * @returns
 */
export function useInputValue(onChange) {
  /**
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns
   */
  const handleChange = ({ target }) => onChange(target.value);
  return { handleChange };
}

/**
 * Hook for sanitizes
 * @param {{ defaultValue: number; onChange: (val: string) => void, max: number}} props
 * @returns
 */
export function useNumericInput({ defaultValue, onChange, max = Infinity }) {
  const [value, setValue] = React.useState(defaultValue?.toString() ?? '');
  const [isValid, setIsValid] = React.useState(Boolean(value));

  React.useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue.toString());
    }
  }, [defaultValue]);

  /**
   * Regex will filter non numericals and still allow for empty spaces at beginning
   */
  const sanitizeInput = React.useCallback(
    (/** @type {string} **/ val) => {
      if (!val.match(Regex.ONLY_NUMBERS)) return;

      // Checks for zero
      if (val === '0') return;

      setValue(parseInt(val) > max ? max.toString() : val);
      setIsValid(Boolean(val));
    },
    [value]
  );

  const { handleChange } = useInputValue(sanitizeInput);

  React.useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [value]);

  return { value, isValid, handleChange };
}

/**
 * Hook for Pagination state
 * @typedef {{ currentPage: ?number, totalPages: number, onChange: OnChange, page: number }} Props
 * @param {Props} props
 * @callback OnChange
 * @param {number} page
 * @param {number} pageLength
 * @returns {void}
 */
export function usePagination({ 
  currentPage, 
  itemCount,
  currentPageLength, 
  onChange 
}) {
  const [page, setPage] = React.useState(Number(currentPage ?? 1));
  const [pageLength, setPageLengthState] = React.useState(Number(currentPageLength));

  React.useEffect(() => {
    onChange?.(page, pageLength);
  }, [page, pageLength]);

  const pageTotal = Math.ceil(itemCount/pageLength);
  const canIncrement = page < pageTotal;
  const canDecrement = page > 1;
  const increment = () => setPage((page) => page + 1);
  const decrement = () => setPage((page) => page - 1);
  const setPageNumber = (val) => setPage(Number(val));
  const setPageLength = (val) => {
    setPage(1);
    setPageLengthState(Number(val));
  };

  return {
    page,
    pageLength,
    pageTotal,
    increment,
    decrement,
    canDecrement,
    canIncrement,
    setPageNumber,
    setPageLength
  };
}
